import { useCallback, useEffect, useRef } from 'react'
import { getSupabase } from '../utils/supabase'
import { supabaseConfig } from '../data/supabaseConfig'
import {
  clockGapMinutes,
  isVpnSuspected,
  localHour,
  resolveVisitorGeo,
  type VisitorGeo
} from '../utils/geo'
import { identifyNetwork } from '../utils/vpnProviders'
import { classifyAccessMethod, readAccessSignals } from '../utils/accessMethods'
import { hasLocaleTension, readLocaleSignals } from '../utils/localeSignals'

const VISITOR_KEY = 'turing.maintenance.visitor'
const HEARTBEAT_MS = 60_000

function visitorId(): string {
  try {
    const existing = window.localStorage.getItem(VISITOR_KEY)
    if (existing) return existing
    const generated = crypto.randomUUID()
    window.localStorage.setItem(VISITOR_KEY, generated)
    return generated
  } catch {
    return '00000000-0000-4000-8000-000000000000'
  }
}

function deviceClass(): 'Mobile' | 'Tablet' | 'Desktop' {
  const ua = navigator.userAgent
  if (/iPad|Tablet/i.test(ua)) return 'Tablet'
  if (/Mobi|Android|iPhone/i.test(ua)) return 'Mobile'
  return 'Desktop'
}

function browserName(): string {
  const ua = navigator.userAgent
  if (!ua) return 'Unknown'
  // Bots and in-app webviews are checked first — they also match Chrome/Safari.
  if (/bot|crawler|spider|monitor|HeadlessChrome|Lighthouse/i.test(ua)) return 'Bot / monitor'
  if (/FBAN|FBAV|Instagram|Line\/|WhatsApp|Snapchat/i.test(ua)) return 'In-app browser'
  if (/Edg\//.test(ua)) return 'Edge'
  if (/OPR\/|Opera/.test(ua)) return 'Opera'
  if (/SamsungBrowser\//.test(ua)) return 'Samsung Internet'
  if (/Chrome\//.test(ua)) return 'Chrome'
  if (/Safari\//.test(ua) && !/Chrome/.test(ua)) return 'Safari'
  if (/Firefox\//.test(ua)) return 'Firefox'
  return 'Other'
}

function osName(): string {
  const ua = navigator.userAgent
  if (/Windows/.test(ua)) return 'Windows'
  if (/Android/.test(ua)) return 'Android'
  if (/iPhone|iPad|iOS/.test(ua)) return 'iOS'
  if (/Mac OS X/.test(ua)) return 'macOS'
  if (/Linux/.test(ua)) return 'Linux'
  return 'Other'
}

interface NetworkInformation {
  /** Speed CLASS, not medium: 'slow-2g' | '2g' | '3g' | '4g'. Fast WiFi reports '4g'. */
  effectiveType?: string
  /** Actual medium: 'wifi' | 'cellular' | 'ethernet' | … Rarely implemented. */
  type?: string
  /** Estimated downlink in Mbps, used to infer a medium when `type` is absent. */
  downlink?: number
}

function networkInfo(): NetworkInformation | undefined {
  return (navigator as Navigator & { connection?: NetworkInformation }).connection
}

/** The effective speed class — comparable to a cellular generation, not a medium. */
function connectionSpeed(): string {
  const effectiveType = networkInfo()?.effectiveType
  return effectiveType ? effectiveType.toUpperCase() : 'Unknown'
}

/**
 * The physical medium, when the browser will say.
 *
 * Only Chromium on Android reliably reports `connection.type`. Everywhere else it
 * is absent, so a device class is used instead: desktops are effectively never on
 * cellular, and a phone reporting a very high downlink is almost certainly on
 * WiFi. Anything less certain stays 'Unknown' rather than being guessed at.
 */
function connectionMedium(): string {
  const info = networkInfo()

  if (info?.type) {
    const type = info.type.toLowerCase()
    if (type === 'wifi') return 'WiFi'
    if (type === 'cellular') return 'Cellular'
    if (type === 'ethernet') return 'Ethernet'
    if (type === 'none') return 'Offline'
    return 'Other'
  }

  const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
  if (!isMobile) return 'WiFi or wired'
  if (info?.downlink !== undefined && info.downlink >= 10) return 'Likely WiFi'
  if (info?.effectiveType && info.effectiveType !== '4g') return 'Likely cellular'
  return 'Unknown'
}

/**
 * The URL the visitor actually asked for. When the edge serves the 503 in place
 * of the requested URL this is just the pathname; when it redirects to a single
 * maintenance URL, the original path is expected in `?from=` (or `?target=`).
 */
function requestedPath(): string {
  try {
    const params = new URLSearchParams(window.location.search)
    const original = params.get('from') ?? params.get('target')
    if (original && original.startsWith('/')) return original.split('?')[0]
    return window.location.pathname || '/'
  } catch {
    return '/'
  }
}

/** Top-level product area, e.g. '/dashboard/payments' → '/dashboard'. */
function pathSection(path: string): string {
  const segment = path.split('/').filter(Boolean)[0]
  return segment ? `/${segment}` : '/'
}

function timeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Unknown'
  } catch {
    return 'Unknown'
  }
}

export type MaintenanceEvent = 'view' | 'retry' | 'support_click' | 'heartbeat' | 'geo'

export interface MaintenanceTelemetry {
  trackRetry: () => void
}

/**
 * Writes maintenance-page events into Supabase: an initial view, a heartbeat
 * every minute while the tab stays open (which powers live concurrency and dwell
 * time), plus retry and support-link interactions. Fire-and-forget — a failed
 * insert never surfaces to the visitor.
 */
export function useMaintenanceTelemetry(): MaintenanceTelemetry {
  const sessionStart = useRef(Date.now())
  // Coarse country/city plus network signals. Never an IP address.
  const geo = useRef<VisitorGeo>({
    country: null,
    city: null,
    ipTimeZone: null,
    vpnFlagged: null,
    networkOrg: null,
    networkAsn: null,
    networkBlock: null
  })

  const send = useCallback((event: MaintenanceEvent) => {
    const supabase = getSupabase()
    if (!supabase) return

    // Origin inference lives entirely in the database (infer_likely_origin), so the
    // raw time zone and language below are the only inputs it needs — that keeps one
    // implementation for live and historical rows alike.
    const network = identifyNetwork(geo.current.networkOrg)
    const locale = readLocaleSignals()

    void supabase
      .from(supabaseConfig.eventsTable)
      .insert({
        host: supabaseConfig.monitoredHost,
        event,
        path: requestedPath(),
        path_section: pathSection(requestedPath()),
        referrer: document.referrer || null,
        device: deviceClass(),
        browser: browserName(),
        os: osName(),
        language: navigator.language || 'unknown',
        connection: connectionSpeed(),
        connection_medium: connectionMedium(),
        viewport_width: window.innerWidth,
        time_zone: timeZone(),
        country: geo.current.country,
        city: geo.current.city,
        ip_time_zone: geo.current.ipTimeZone,
        vpn_suspected: isVpnSuspected(geo.current),
        network_org: network?.label ?? null,
        network_asn: geo.current.networkAsn,
        // Network block only — never a full IP address.
        network_block: geo.current.networkBlock,
        network_class: network?.networkClass ?? null,
        access_method: classifyAccessMethod(
          readAccessSignals(network?.networkClass ?? null, timeZone())
        ),
        clock_gap_minutes: clockGapMinutes(geo.current),
        local_hour: localHour(),
        // Locale internals, which survive time-zone spoofing. Interpreted in the
        // database so live and historical rows use one implementation.
        calendar: locale.calendar,
        numbering_system: locale.numberingSystem,
        languages: locale.languages,
        locale_tension: hasLocaleTension(locale, navigator.language || ''),

        visitor_id: visitorId(),
        seconds_on_page: Math.round((Date.now() - sessionStart.current) / 1000)
      })
      .then(undefined, () => {
        /* telemetry is best-effort */
      })
  }, [])

  useEffect(() => {
    let cancelled = false

    // The view is recorded immediately — geography must never gate it.
    send('view')

    // Geography resolves in the background, off the critical path, and is
    // attached to a follow-up row plus every later event in this session. The
    // visitor never waits on it and sees nothing.
    const resolveInBackground = () => {
      void resolveVisitorGeo().then(resolved => {
        if (cancelled || (!resolved.country && !resolved.city && !resolved.ipTimeZone)) return
        geo.current = resolved
        send('geo')
      })
    }

    const idle = window.requestIdleCallback
    const idleHandle = idle
      ? idle(resolveInBackground, { timeout: 3000 })
      : window.setTimeout(resolveInBackground, 1200)

    const heartbeat = window.setInterval(() => {
      if (document.visibilityState === 'visible') send('heartbeat')
    }, HEARTBEAT_MS)

    const onSupportClick = (nativeEvent: MouseEvent) => {
      const target = nativeEvent.target as HTMLElement | null
      if (target?.closest('a[href^="mailto:"]')) send('support_click')
    }

    document.addEventListener('click', onSupportClick)

    return () => {
      cancelled = true
      if (idle && window.cancelIdleCallback) window.cancelIdleCallback(idleHandle as number)
      else window.clearTimeout(idleHandle as number)
      window.clearInterval(heartbeat)
      document.removeEventListener('click', onSupportClick)
    }
  }, [send])

  return { trackRetry: useCallback(() => send('retry'), [send]) }
}
